import React from 'react';
import { SlaBands } from '../../../interfaces/panel-data';
import { countOf, RowUnit } from './sla-row-unit';
import './BandBar.scss';

/** Scan order: best outcome first, absence of judgement last. Also the legend's order. */
const SEGMENTS: Array<{ band: keyof SlaBands; modifier: string; label: string }> = [
  { band: 'within', modifier: 'within', label: 'Within SLA' },
  { band: 'atRisk', modifier: 'at-risk', label: 'At risk' },
  { band: 'breaching', modifier: 'breaching', label: 'Breaching' },
  { band: 'notRegistered', modifier: 'not-registered', label: 'No budget' },
];

interface BandBarProps {
  /** Null means no budget is configured for this group, so there is nothing to band its rows against. */
  bands: SlaBands | null;
  /**
   * What this group's rows are, so a segment says `Breaching: 3 deltas` rather than `Breaching: 3`. The bar
   * is the one place on the panel where a count appears with no heading above it and no row label beside it —
   * a hover that answers "three of what?" with a bare number has told the reader the part they already knew.
   */
  rowUnit: RowUnit;
  /** Rendered in place of the track when `bands` is null. */
  noBudgetText: string;
  /** Rendered in place of the track when a budget exists but nothing landed in a band. */
  nothingJudgedText: string;
}

/**
 * How one group's rows sat against their budget: a full-width track split into four proportional segments.
 *
 * A sibling of `StageBar` rather than a mode of it. `StageBar`'s two segments mean kept and lost, and its
 * lost half is hatched so the split survives greyscale; four bands are four meanings and four solid fills,
 * and one component holding both vocabularies would have to be read twice to be understood once.
 *
 * With nothing to band the component renders a sentence and no track at all. An empty track reads as *good* —
 * which is the whole failure this panel exists to prevent.
 */
export const BandBar: React.FC<BandBarProps> = ({
  bands,
  rowUnit,
  noBudgetText,
  nothingJudgedText,
}) => {
  if (!bands) {
    return <span className="ovp-bandbar__none">{noBudgetText}</span>;
  }

  const counts = SEGMENTS.map((segment) => bands[segment.band]);
  if (counts.some((count) => !Number.isFinite(count) || count < 0)) {
    // Malformed, unlike the empty group below: there is no proportion to take, and the panel's ErrorBoundary
    // turning this into "failed to load" is the truth. Four zero-width segments would not be.
    throw new Error(`SLA group carried unusable band counts: ${counts.join(', ')}`);
  }

  const total = counts.reduce((sum, count) => sum + count, 0);
  if (total === 0) {
    // A group holding rows none of which reached a band, which the payload does produce: its count comes from
    // the index and its bands from judging each row, so a row with no elapsed to judge — finished without a
    // `metricValue`, or in flight and never fetched — is counted in the first and absent from the second.
    return <span className="ovp-bandbar__none">{nothingJudgedText}</span>;
  }

  const present = SEGMENTS.filter((segment) => bands[segment.band] > 0);

  return (
    <span
      className="ovp-bandbar"
      role="img"
      aria-label={present
        .map((segment) => `${segment.label}: ${countOf(bands[segment.band], rowUnit)}`)
        .join(', ')}
    >
      {present.map((segment) => (
        <span
          key={segment.band}
          className={`ovp-bandbar__segment ovp-bandbar__segment--${segment.modifier}`}
          style={{ width: `${(bands[segment.band] / total) * 100}%` }}
          title={`${segment.label}: ${countOf(bands[segment.band], rowUnit)} of ${countOf(
            total,
            rowUnit
          )} judged`}
          data-testid={`band-segment-${segment.modifier}`}
        />
      ))}
    </span>
  );
};
